import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TopicService } from '../topic.service';
import { MessageService } from '../../shared/message.service';
import { CompressionService } from '../compression.service';
import { GeneralExerciseComponent } from '../../General/g-exercise.component';
import * as shape from 'd3-shape';
import { GraphComponent } from '../../shared/graph/graph.component';

@Component({
  selector: 'app-create-compression',
  templateUrl: './create-compression.component.html',
  styleUrls: ['./create-compression.component.css']
})
export class CreateCompressionComponent extends GeneralExerciseComponent {

  mapping: string[][];

  get codes(): string[] {
    return this.mapping.map(v => v[2]);
  }

  prefixes: string[] = [];

  greyout: boolean;

  errors: string[] = [];

  nodes: any[];
  edges: any[];

  @ViewChild(GraphComponent, {static:true}) graph: GraphComponent;

  view: any[] = undefined;
  fitContainer: boolean = true;
  curve = shape.curveBundle;

  constructor(activatedRoute: ActivatedRoute, topicService: TopicService, messageService: MessageService, protected compressionService: CompressionService) {
    super(activatedRoute, topicService, messageService)
  }

  setTypeSpecificsFromConfiguration() {
    this.mapping = [];
    for (let m of this.configuration.typespecifics.mapping) {
      this.mapping.push([...m]);
    }

    this.greyout = this.configuration.typespecifics.greyOut;

    let graph = this.compressionService.computeTreeBySize(this.mapping);
    this.nodes = graph.nodes;
    this.edges = graph.edges;

    this.prefixes = [];

    this.printable = true;

  }


  setTypeSpecificsFromState() {
    if (this.state.typespecific) {
      if (this.state.typespecific.mapping) {
        this.mapping = this.state.typespecific.mapping;
      }
    }
    this.computePrefixes();
  }

  computeState() {
    this.state.typespecific.mapping = this.mapping;
  }

  checkSolution() {
    let mapping_temp = [...this.mapping];
    mapping_temp.sort((a, b) => +a[1] - +b[1]);

    let error = false;


    this.errors = this.mapping.filter(m => m[2] == "" || m[2] == undefined).map(m => m[0]);

    if (this.errors.length > 0) {
      this.messageService.addAlertWithTimeout(this.get("notCompletlyFilled"), "danger", 10000);
      return;
    }

    this.errors = [];

    //check for prefix-freedom
    for (let i = 0; i < mapping_temp.length; i++) {
      for (let j = 0; j < mapping_temp.length; j++) {
        if (i != j && mapping_temp[i][2].startsWith(mapping_temp[j][2])) {
          error = true;
          this.errors.push(mapping_temp[i][0], mapping_temp[j][0]);
        }
      }
    }

    if (error) {
      this.messageService.addAlertWithTimeout(this.get("notPrefixFree"), "danger", 10000);
      return;
    }

    //check for increasing code length
    for (let i = 0; i < this.mapping.length; i++) {
      for (let j = 0; j < i; j++) {
        if (this.mapping[i][1] < this.mapping[j][1] && this.mapping[i][2].length < this.mapping[j][2].length) {
          error = true;
          this.errors.push(this.mapping[i][0], this.mapping[j][0]);
        }
      }
    }

    if (error) {
      this.messageService.addAlertWithTimeout(this.get("lessFrequentLonger"), "danger", 10000);
      return;
    }

    //check for maximal code length
    for (let i = 0; i < this.mapping.length; i++) {
      if (this.mapping[i][2].length > Math.ceil(Math.log2(100 / (+this.mapping[i][1])))) {
        error = true;
        this.errors.push(this.mapping[i][0]);
      }
    }

    if (error) {
      this.messageService.addAlertWithTimeout(this.get("longerThanNeeded"), "danger", 10000);
      return;
    }

    this.messageService.addAlertWithTimeout(this.get("correct"), "success", 10000);
    this.solved = true;
    this.state.solved = true;
    this.saveState();


  }

  evaluate(): number[] {
    let mapping_temp = [...this.mapping];
    mapping_temp.sort((a, b) => +a[1] - +b[1]);

    let errors = this.mapping.filter(m => m[2] == "" || m[2] == undefined).map(m => m[0]);

    //check for prefix-freedom
    for (let i = 0; i < mapping_temp.length; i++) {
      for (let j = 0; j < mapping_temp.length; j++) {
        if (mapping_temp[i][2] != undefined && i != j) {
          if (mapping_temp[i][2].startsWith(mapping_temp[j][2])) {
            errors.push(mapping_temp[i][0], mapping_temp[j][0]);
          }
        }
      }
    }

    //check for incrementing code length
    for (let i = 0; i < this.mapping.length; i++) {
      for (let j = 0; j < i; j++) {
        if (this.mapping[i][2] != undefined && this.mapping[j][2] != undefined) {
          if (this.mapping[i][1] < this.mapping[j][1] && this.mapping[i][2].length < this.mapping[j][2].length) {
            errors.push(this.mapping[i][0], this.mapping[j][0]);
          }
        }
      }
    }

    //check for maximal code length
    for (let i = 0; i < this.mapping.length; i++) {
      if (this.mapping[i][2] != undefined) {
        if (this.mapping[i][2].length > Math.ceil(Math.log2(100 / (+this.mapping[i][1])))) {
          errors.push(this.mapping[i][0]);
        }
      }
    }

    errors = errors.filter((v, i) => errors.indexOf(v) == i);

    return [(this.mapping.length - errors.length) * 0.5, this.mapping.length * 0.5];
  }

  /** add the node with given label to the codes */
  selectNode(label: string) {
    if ((this.prefixes.includes(label)) || this.codes.includes(label) || label == "start") {
      return;
    }
    let i = 0;
    while (i < this.mapping.length) {
      if (this.codes[i] == "" || this.codes[i] == undefined) {
        this.mapping[i][2] = label;
        break;
      }
      i++;
    }
    this.computePrefixes();

  }

  /** when a code was changed, call this method */
  changeCode(i: number) {
    this.computePrefixes();
  }

  /** compute all the prefixes of the selected codes */
  computePrefixes() {
    if (this.greyout) {
      this.prefixes = [];
      for (let n of this.nodes.map(v => v.id)) {
        for (let c of this.codes) {
          if (n.startsWith(c) && c != "") {
            this.prefixes.push(n);
            break;
          }
        }
      }
    }
  }
}

